import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Crosshair, CurrencyDollar, Ticket, Trash, Key, Plus, Lock } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { ChannelPicker } from "@/pages/admin/Listings";

const L = ({ t, children }) => <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#888] block mb-1">{t}</span>{children}</label>;
const Box = ({ title, children, id }) => <section className="border border-[#2a2a2a] bg-[#0a0a0a] p-5" data-testid={id}><div className="font-display text-2xl mb-3">{title}</div>{children}</section>;
const Row = ({ children, testid }) => <div className="flex items-center gap-3 border border-white/10 p-2 text-xs" data-testid={testid}>{children}</div>;

function Watches() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-watches"], queryFn: () => api.get("/admin/watches").then((r) => r.data), refetchInterval: 10000 });
  const live = data.filter((w) => w.status === "watching");
  return (
    <Box title={`Sniper watchlist (${live.length} watching)`} id="overpowered-watches">
      <p className="text-xs text-[#777] mb-3">Users run <code className="text-[#8c96ff]">/snipe</code> in Discord. Checked every ~5 min, turbo every ~90s when a name looks free. Wins DM the watcher + open a claim ticket.</p>
      <div className="max-h-64 overflow-auto space-y-2" data-lenis-prevent>
        {data.length === 0 && <div className="text-xs text-[#777]" data-testid="watches-empty">No watched names yet.</div>}
        {data.map((w) => (
          <Row key={w.id} testid={`watch-row-${w.id}`}>
            <Crosshair size={16} className={w.status === "watching" ? "text-[#35d07f]" : "text-[#777]"} />
            <div className="flex-1"><span className="text-[#ffb020]">{w.name}</span> <span className="text-[#777]">{w.status} · {w.checks || 0} checks{w.user_id ? " · discord" : ""}{w.discord_contact ? ` · ${w.discord_contact}` : ""}</span></div>
            {w.status === "watching" && <button className="btn-ghost !py-1 !px-2 !text-[10px]" onClick={async () => { await api.delete(`/admin/watches/${w.id}`); qc.invalidateQueries({ queryKey: ["admin-watches"] }); }} data-testid={`watch-cancel-${w.id}`}>Stop</button>}
          </Row>
        ))}
      </div>
    </Box>
  );
}

function Offers() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-offers"], queryFn: () => api.get("/admin/offers").then((r) => r.data), refetchInterval: 10000 });
  const pending = data.filter((o) => o.status === "pending");
  return (
    <Box title={`Offers (${pending.length} pending)`} id="overpowered-offers">
      <p className="text-xs text-[#777] mb-3">Buyers hit Make offer on OBO drops. Decide in Discord (accept / counter / decline) or cancel here.</p>
      <div className="max-h-64 overflow-auto space-y-2" data-lenis-prevent>
        {data.length === 0 && <div className="text-xs text-[#777]" data-testid="offers-empty">No offers yet.</div>}
        {data.map((o) => (
          <Row key={o.id} testid={`offer-row-${o.id}`}>
            <CurrencyDollar size={16} className={o.status === "pending" ? "text-[#ffb020]" : "text-[#777]"} />
            <div className="flex-1"><span className="text-[#ffb020]">{o.name}</span> <span className="text-white">{o.amount_m}M</span> <span className="text-[#777]">by {o.buyer_name || o.buyer_id} · {o.status}{o.decided_by ? ` · ${o.decided_by}` : ""}</span></div>
            {o.status === "pending" && <button className="btn-ghost !py-1 !px-2 !text-[10px]" onClick={async () => { await api.delete(`/admin/offers/${o.id}`); qc.invalidateQueries({ queryKey: ["admin-offers"] }); }} data-testid={`offer-cancel-${o.id}`}>Cancel</button>}
          </Row>
        ))}
      </div>
    </Box>
  );
}

function Claims() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-claims"], queryFn: () => api.get("/admin/claims").then((r) => r.data), refetchInterval: 10000 });
  const [name, setName] = useState("");
  const [buyer, setBuyer] = useState("");
  const open = async () => {
    try { await api.post("/admin/claims", { name, discord_contact: buyer }); setName(""); setBuyer(""); qc.invalidateQueries({ queryKey: ["admin-claims"] }); toast.success("Claim ticket opening in Discord"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Box title="Claims (snipe wins + transfer-to-buyer)" id="overpowered-claims">
      <p className="text-xs text-[#777] mb-3">Snipe wins open these automatically. Open one manually when a name frees, walk the 3-step swap ladder in the ticket.</p>
      <div className="flex flex-wrap gap-2 mb-3 items-end">
        <div className="flex-1 min-w-40"><L t="Name that freed"><input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="rain" data-testid="claim-name-input" /></L></div>
        <div className="flex-1 min-w-40"><L t="Buyer discord (optional)"><input className="field" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="@buyer" data-testid="claim-buyer-input" /></L></div>
        <button className="btn-bomb !py-2 flex items-center gap-2" disabled={!name.trim()} onClick={open} data-testid="claim-open-button"><Ticket size={16} /> Open claim</button>
      </div>
      <div className="max-h-64 overflow-auto space-y-2" data-lenis-prevent>
        {data.length === 0 && <div className="text-xs text-[#777]" data-testid="claims-empty">No claims yet.</div>}
        {data.map((c) => (
          <Row key={c.id} testid={`claim-row-${c.id}`}>
            <Ticket size={16} className={c.status === "ready" ? "text-[#35d07f]" : "text-[#777]"} />
            <div className="flex-1"><span className="text-[#ffb020]">{c.name}</span> <span className="text-[#777]">{c.id} · {c.status}{c.buyer_name ? ` · buyer ${c.buyer_name}` : ""}</span></div>
          </Row>
        ))}
      </div>
    </Box>
  );
}

function PricePanels() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-price-panels"], queryFn: () => api.get("/admin/price-panels").then((r) => r.data) });
  const [tier, setTier] = useState("S");
  const [chan, setChan] = useState("");
  const post = async () => {
    try { await api.post("/admin/price-panels", { tier, channel_id: chan }); setChan(""); qc.invalidateQueries({ queryKey: ["admin-price-panels"] }); toast.success(`${tier}-tier live list posted — auto-updates on every sale`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Box title="Live price lists (auto-updating)" id="overpowered-panels">
      <p className="text-xs text-[#777] mb-3">One live embed per tier. Auto-edits on every sale, new drop and restock — never goes stale like pasted lists.</p>
      <div className="flex flex-wrap gap-2 mb-3 items-end">
        <div className="w-28"><L t="Tier"><select className="field" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="panel-tier-select">{["S", "A", "B", "C", "D"].map((t) => <option key={t} value={t}>{t}</option>)}</select></L></div>
        <div className="flex-1 min-w-52"><L t="Channel"><ChannelPicker value={chan} onChange={setChan} testid="panel-channel-select" allowDefault={false} /></L></div>
        <button className="btn-bomb !py-2" disabled={!chan} onClick={post} data-testid="panel-post-button">Post live list</button>
        <button className="btn-ghost !py-2" onClick={async () => { try { await api.post("/admin/price-panels/refresh"); toast.success("All live lists refreshing"); } catch (e) { toast.error(errMsg(e)); } }} data-testid="panel-refresh-button">Refresh all</button>
      </div>
      <div className="space-y-2">
        {data.length === 0 && <div className="text-xs text-[#777]" data-testid="panels-empty">No live lists yet — post S first, then A–D.</div>}
        {data.map((p) => (
          <Row key={p.tier} testid={`panel-row-${p.tier}`}>
            <div className="flex-1"><span className="text-[#ffb020]">{p.tier}-TIER</span> <span className="text-[#777]">updated {p.updated_at ? new Date(p.updated_at).toLocaleString() : "never"}</span></div>
          </Row>
        ))}
      </div>
    </Box>
  );
}

function Claimers() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-claimers"], queryFn: () => api.get("/admin/claimers").then((r) => r.data) });
  const [f, setF] = useState({ label: "", kind: "launcher", login: "", password: "" });
  const add = async () => {
    try { await api.post("/admin/claimers", f); setF({ label: "", kind: "launcher", login: "", password: "" }); qc.invalidateQueries({ queryKey: ["admin-claimers"] }); toast.success("Claimer stored in the encrypted vault"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Box title="Claimer vault (burner accounts, encrypted)" id="overpowered-claimers">
      <p className="text-xs text-[#777] mb-3 flex items-start gap-2"><Lock size={14} className="mt-0.5 shrink-0" /> Legacy or launcher logins for future instant auto-claim. Passwords are Fernet-encrypted — the panel never shows them back. {data && !data.vault && <span className="text-[#ff2a55]">VAULT LOCKED — set CLAIM_VAULT_KEY on the server.</span>}</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <L t="Label"><input className="field" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Burner 1" data-testid="claimer-label-input" /></L>
        <L t="Kind"><select className="field" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="claimer-kind-select"><option value="launcher">Jagex launcher</option><option value="legacy">Legacy login</option></select></L>
        <L t="Login / email"><input className="field" autoComplete="off" value={f.login} onChange={(e) => setF({ ...f, login: e.target.value })} data-testid="claimer-login-input" /></L>
        <L t="Password"><input type="password" autoComplete="new-password" className="field" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="claimer-password-input" /></L>
      </div>
      <button className="btn-bomb !py-2 flex items-center gap-2" disabled={!f.label.trim() || !f.login.trim() || !f.password} onClick={add} data-testid="claimer-add-button"><Plus size={16} /> Store claimer</button>
      <div className="mt-3 space-y-2">
        {(data?.accounts || []).map((a) => (
          <Row key={a.id} testid={`claimer-row-${a.id}`}>
            <Key size={16} className="text-[#35d07f]" />
            <div className="flex-1"><span className="text-white">{a.label}</span> <span className="text-[#777]">{a.kind} · {a.status}</span></div>
            <button className="btn-ghost !py-1 !px-2 !text-[10px]" onClick={async () => { if (window.confirm(`Delete ${a.label}?`)) { await api.delete(`/admin/claimers/${a.id}`); qc.invalidateQueries({ queryKey: ["admin-claimers"] }); } }} data-testid={`claimer-delete-${a.id}`}><Trash size={12} /></button>
          </Row>
        ))}
      </div>
    </Box>
  );
}

export default function Overpowered() {
  return (
    <div data-testid="admin-overpowered">
      <div className="mb-4"><div className="font-display text-4xl">Overpowered</div><div className="text-xs text-[#777] uppercase tracking-[0.25em]">// sniper · offers · claims · live lists · vault</div></div>
      <div className="grid xl:grid-cols-2 gap-4">
        <Watches />
        <Offers />
        <Claims />
        <PricePanels />
        <Claimers />
        <Box title="Drop alerts role" id="overpowered-drops">
          <p className="text-xs text-[#777] mb-3">S/A-tier drops ping this role in the drop channel. Paste the role ID in Admin → Discord → Connect, then save.</p>
          <a href="/admin/discord" className="btn-ghost !py-2 inline-block" data-testid="overpowered-drops-link">Set role in Discord settings</a>
        </Box>
      </div>
    </div>
  );
}
